/**
 * KEJJA — Home theme provider.
 * Reads home.theme and applies CSS variables to <html>.
 * Other components opt-in by referencing var(--home-primary), var(--home-accent), etc.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface HomeTheme {
  preset?: string | null;
  primary?: string | null;
  accent?: string | null;
  warm?: boolean | null;
}

interface ThemeCtx {
  theme: HomeTheme;
  setTheme: (t: HomeTheme) => void;
  isLoading: boolean;
}

const Ctx = createContext<ThemeCtx>({
  theme: { preset: 'classic' },
  setTheme: () => {},
  isLoading: false,
});

export function HomeThemeProvider({ homeId, children }: { homeId: string | undefined; children: ReactNode }) {
  const [local, setLocal] = useState<HomeTheme>({ preset: 'classic' });

  const themeQ = useQuery({
    queryKey: ['home-theme', homeId],
    queryFn: () => api.get<{ theme: HomeTheme }>(`/homes/${homeId}/theme`),
    enabled: !!homeId,
  });

  // Apply incoming theme to local + DOM
  useEffect(() => {
    if (themeQ.data?.theme) {
      setLocal(themeQ.data.theme);
    }
  }, [themeQ.data]);

  useEffect(() => {
    applyToRoot(local);
  }, [local]);

  return (
    <Ctx.Provider value={{ theme: local, setTheme: setLocal, isLoading: themeQ.isLoading }}>
      {children}
    </Ctx.Provider>
  );
}

export function useHomeTheme() {
  return useContext(Ctx);
}

function applyToRoot(theme: HomeTheme) {
  const root = document.documentElement;
  root.style.setProperty('--home-primary', theme.primary || '#196287');
  root.style.setProperty('--home-accent',  theme.accent  || '#0ea5e9');

  // Compute helper variables
  const primaryRgb = hexToRgb(theme.primary || '#196287');
  const accentRgb  = hexToRgb(theme.accent  || '#0ea5e9');
  if (primaryRgb) {
    root.style.setProperty('--home-primary-rgb', `${primaryRgb.r} ${primaryRgb.g} ${primaryRgb.b}`);
  }
  if (accentRgb) {
    root.style.setProperty('--home-accent-rgb', `${accentRgb.r} ${accentRgb.g} ${accentRgb.b}`);
  }

  // Warm body bg overlay (subtle)
  if (theme.warm) {
    document.body.classList.add('home-warm');
  } else {
    document.body.classList.remove('home-warm');
  }
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return null;
  return { r: parseInt(m[1]!, 16), g: parseInt(m[2]!, 16), b: parseInt(m[3]!, 16) };
}
